import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Typography,
    Button,
    Select,
    MenuItem,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { BreakRuleConditionType, BreakRuleActionType } from '../../../../Enums';
import DurationInput from './DurationInput';
import './BreakCalculation.scss';

const StyledSelect = (props) => (
    <Select
        MenuProps={{
            anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
            },
            transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
            },
        }}
        variant="outlined"
        className="select-full-width"
        defaultValue=""
        {...props}
    >
        {props.children}
    </Select>
);

/**
 * @param {{
 *  breakRules: *[],
 *  handleChangeSetting: (target: string, value: any) => void,
 *  disabled?: boolean
 * }} param0
 */
function BreakCalculation({ breakRules = [], handleChangeSetting, disabled = false }) {
    const { t } = useTranslation('employmentBillboard', { keyPrefix: 'settings.ruleSet.breakRuleStep' });

    const conditionTypeSelectOptions = Object.keys(BreakRuleConditionType).map((type) => ({
        label: t(`breakRuleConditionType.${type}`),
        value: BreakRuleConditionType[type],
    }));

    const actionTypeSelectOptions = Object.keys(BreakRuleActionType).map((action) => ({
        label: t(`breakRuleActionType.${action}`),
        value: BreakRuleActionType[action],
    }));

    const handleAddBreakRule = () => {
        const lastId = Math.max(...breakRules.map((rule) => rule.id));

        const emptyBreakRule = {
            condition: 0,
            shiftDuration: 0.0,
            action: 0,
            actionDuration: 0.0,
            id: lastId < 0 ? 0 : lastId + 1,
        };
        const newBreakRules = [...breakRules, emptyBreakRule];

        handleChangeSetting('breakRules', newBreakRules);
    };

    const handleDeleteBreakRule = (breakRuleId) => {
        const newBreakRules = breakRules.filter((rule) => rule.id !== breakRuleId);

        handleChangeSetting('breakRules', newBreakRules);
    };

    const handleChangeBreakRule = (target, input, breakRuleId) => {
        const newBreakRules = breakRules.map((rule) =>
            (rule.id === breakRuleId ? { ...rule, [target]: input } : rule));
        handleChangeSetting('breakRules', newBreakRules);
    };

    const generateBreakRuleLine = (breakRule) => (
        <div className="break-rule-Line" key={`break-rule-${breakRule.id}`}>
            <div className="break-rule-item-large">
                <StyledSelect
                    id="break-rule-select"
                    renderValue={() => conditionTypeSelectOptions.find((x) => x.value === breakRule.condition)?.label}
                    value={conditionTypeSelectOptions.find((x) => x.value === breakRule.condition)?.value ?? ''}
                    onChange={(/** @type {React.ChangeEvent<{ name?: string; value: number }>} */
                        event,
                    ) => handleChangeBreakRule('condition', event.target.value, breakRule.id)}
                    disabled={disabled}
                >
                    {conditionTypeSelectOptions.map((x) => (
                        <MenuItem
                            value={x.value}
                            key={`SelectItem_${x.label}`}
                        >
                            {x.label}
                        </MenuItem>
                    ))}
                </StyledSelect>
            </div>
            <div className="break-rule-item-medium">
                <DurationInput
                    key={`condition-value-${breakRule.id}`}
                    onChange={(value) => handleChangeBreakRule('shiftDuration', value, breakRule.id)}
                    value={breakRule.shiftDuration}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    error={breakRule.shiftDuration === 0}
                    disabled={disabled}
                />

            </div>
            <div className="break-rule-item-large">
                <StyledSelect
                    id="break-rule-select"
                    renderValue={() => actionTypeSelectOptions.find((x) => x.value === breakRule.action)?.label}
                    value={actionTypeSelectOptions.find((x) => x.value === breakRule.action)?.value ?? ''}
                    onChange={(/** @type {React.ChangeEvent<{ name?: string; value: number }>} */
                        event,
                    ) => handleChangeBreakRule('action', event.target.value, breakRule.id)}
                    disabled={disabled}
                >
                    {actionTypeSelectOptions.map((x) => (
                        <MenuItem
                            value={x.value}
                            key={`SelectItem_${x.label}`}
                        >
                            {x.label}
                        </MenuItem>
                    ))}
                </StyledSelect>
            </div>
            <div className="break-rule-item-medium">
                <DurationInput
                    key={`action-value-${breakRule.id}`}
                    onChange={(value) => handleChangeBreakRule('actionDuration', value, breakRule.id)}
                    value={breakRule.actionDuration}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    error={breakRule.actionDuration === 0}
                    disabled={disabled}
                />
            </div>
            <div className="break-rule-item-small">
                <Button
                    onClick={() => handleDeleteBreakRule(breakRule.id)}
                    disabled={disabled}
                >
                    <FontAwesomeIcon icon={faTrash} />
                </Button>
            </div>
        </div>
    );

    return (
        <div className="break-rule-container">
            <Typography className="regular-label">
                {t('conditionsTitle')}
            </Typography>
            <div className="break-rules-container">
                <div className="break-rule-Line">
                    <div className="break-rule-item-large small-label">
                        {t('tableHeader1')}
                    </div>
                    <div className="break-rule-item-medium small-label">
                        {t('tableHeader2')}
                    </div>
                    <div className="break-rule-item-large small-label">
                        {t('tableHeader3')}
                    </div>
                    <div className="break-rule-item-medium small-label">
                        {t('tableHeader4')}
                    </div>
                </div>
                {breakRules.map((breakRule) => generateBreakRuleLine(breakRule))}
            </div>
            <Button
                size="small"
                color="primary"
                onClick={handleAddBreakRule}
                disabled={disabled}
            >
                {t('buttonLabel')}
            </Button>
        </div>
    );
}
export default BreakCalculation;
